import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import ThemeProvider from "@/components/providers/ThemeProvider";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ScrollToTop from "@/components/ScrollToTop";
import { useReleaseStuckPointerEvents } from "@/hooks/use-release-stuck-pointer-events";

// Eager load homepage for instant display
import Index from "./pages/Index";

// Lazy load other routes to reduce initial bundle
const Audios = lazy(() => import("./pages/Audios"));
const Argumentos = lazy(() => import("./pages/Argumentos"));
const Cortometrajes = lazy(() => import("./pages/Cortometrajes"));
const Guiones = lazy(() => import("./pages/Guiones"));
const Contacto = lazy(() => import("./pages/Contacto"));
const Literatura = lazy(() => import("./pages/Literatura"));
const ProyectosSeries = lazy(() => import("./pages/ProyectosSeries"));
const TelevisionRadio = lazy(() => import("./pages/TelevisionRadio"));
const Teatro = lazy(() => import("./pages/Teatro"));
const Cooperativas = lazy(() => import("./pages/Cooperativas"));
const ObraProducida = lazy(() => import("./pages/ObraProducida"));
const FuentesRojoAmanecer = lazy(() => import("./pages/FuentesRojoAmanecer"));
const Auth = lazy(() => import("./pages/Auth"));
const Admin = lazy(() => import("./pages/Admin"));
const Styleguide = lazy(() => import("./pages/Styleguide"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Create QueryClient directly to avoid import issues
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 10, // 10 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
});

const App = () => {
  useReleaseStuckPointerEvents();

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
            <ScrollToTop />
            <Suspense fallback={<div className="min-h-screen flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/audios" element={<Audios />} />
                <Route path="/argumentos" element={<Argumentos />} />
                <Route path="/cortometrajes" element={<Cortometrajes />} />
                <Route path="/guiones" element={<Guiones />} />
                
                <Route path="/literatura" element={<Literatura />} />
                <Route path="/proyectos-series" element={<ProyectosSeries />} />
                <Route path="/television-radio" element={<TelevisionRadio />} />
                <Route path="/teatro" element={<Teatro />} />
                <Route path="/cooperativas" element={<Cooperativas />} />
                <Route path="/obra-producida" element={<ObraProducida />} />
                <Route path="/fuentes-rojo-amanecer" element={<FuentesRojoAmanecer />} />
                <Route path="/contacto" element={<Contacto />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/styleguide" element={<Styleguide />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
